'use client';
import React, { useState, useEffect } from 'react';
import { Send, Loader2, MessageSquare } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import StarRating from '@/components/ui/StarRating';
import LikeButton from '@/components/ui/LikeButton';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DBComment {
  id: string;
  user_id: string;
  author_role: 'poster' | 'entrepreneur' | 'investor';
  rating: number;
  content: string;
  likes_count: number;
  created_at: string;
  user_profiles: {
    full_name: string;
    reputation: number;
    role: string;
  } | null;
}

interface CommentSectionProps {
  problemId: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function CommentSection({ problemId }: CommentSectionProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<DBComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [ratingValue, setRatingValue] = useState(0);
  const [text, setText] = useState('');
  const [textError, setTextError] = useState('');
  const [ratingError, setRatingError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!problemId) return;
    fetchComments();
  }, [problemId]);

  async function fetchComments() {
    setLoadingComments(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('comments')
      .select('id, user_id, author_role, rating, content, likes_count, created_at, user_profiles(full_name, reputation, role)')
      .eq('problem_id', problemId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setComments(data as DBComment[]);
    }
    setLoadingComments(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let valid = true;
    if (ratingValue === 0) { setRatingError('Please select a star rating.'); valid = false; } else setRatingError('');
    if (!text.trim()) { setTextError('Comment text is required.'); valid = false; }
    else if (text.trim().length < 20) { setTextError('Comments must be at least 20 characters.'); valid = false; }
    else setTextError('');
    if (!valid) return;

    if (!user) {
      window.location.href = '/sign-up-login-screen';
      return;
    }

    setIsSubmitting(true);
    const supabase = createClient();

    // Get user profile to determine role
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    const authorRole = (profile?.role as 'poster' | 'entrepreneur' | 'investor') || 'poster';

    const { error } = await supabase.from('comments').insert({
      problem_id: problemId,
      user_id: user.id,
      author_role: authorRole,
      rating: ratingValue,
      content: text.trim(),
      likes_count: 0,
    });

    if (!error) {
      setText('');
      setRatingValue(0);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
      await fetchComments();
    }
    setIsSubmitting(false);
  }

  return (
    <div className="p-5 space-y-5">
      {/* Add Comment */}
      <div className="bg-muted/40 rounded-xl p-4 border border-border">
        <p className="text-xs font-semibold text-foreground mb-3">Leave a Comment & Rating</p>
        {!user && (
          <p className="text-xs text-muted-foreground mb-3">
            <a href="/sign-up-login-screen" className="text-primary hover:underline font-semibold">Sign in</a> to post a comment.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Your Rating</label>
            <StarRating value={ratingValue} interactive onChange={setRatingValue} size={20} />
            {ratingError && <p className="mt-1 text-xs text-danger font-medium">{ratingError}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor={`comment-text-${problemId}`}>
              Comment
            </label>
            <textarea
              id={`comment-text-${problemId}`}
              rows={3}
              value={text}
              onChange={(e) => { setText(e.target.value); if (textError) setTextError(''); }}
              placeholder="Share your perspective on this problem — market insights, technical feasibility, data points..."
              className={`w-full px-3 py-2.5 bg-card border rounded-xl text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-none ${textError ? 'border-danger' : 'border-input'}`}
              disabled={!user}
            />
            {textError && <p className="mt-1 text-xs text-danger font-medium">{textError}</p>}
          </div>
          {submitSuccess && (
            <p className="text-xs text-success font-medium">Comment posted successfully!</p>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !user}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70"
          >
            {isSubmitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {isSubmitting ? 'Posting...' : 'Post Comment'}
          </button>
        </form>
      </div>

      {/* Comments List */}
      {loadingComments ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-primary" />
        </div>
      ) : comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
            <MessageSquare size={18} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-semibold text-foreground">No comments yet</p>
          <p className="text-xs text-muted-foreground text-center">
            Be the first to share your insights on this problem.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {comments.length} Comment{comments.length !== 1 ? 's' : ''}
          </p>
          {comments.map((comment) => {
            const authorName = comment.user_profiles?.full_name || 'Anonymous';
            const authorRole = comment.author_role;
            const reputation = comment.user_profiles?.reputation ?? 0;
            return (
              <div key={comment.id} className="bg-card border border-border rounded-xl p-4 space-y-2.5">
                {/* Author */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      authorRole === 'investor' ? 'bg-primary/10' :
                      authorRole === 'entrepreneur' ? 'bg-violet-100' : 'bg-orange-100'
                    }`}>
                      <span className={`text-xs font-bold ${
                        authorRole === 'investor' ? 'text-primary' :
                        authorRole === 'entrepreneur' ? 'text-violet-600' : 'text-orange-600'
                      }`}>
                        {authorName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground leading-none">{authorName}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <StatusBadge variant={authorRole} size="sm" />
                        <span className="text-xs text-success font-semibold">⭐ {reputation}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {comment.rating > 0 && <StarRating value={comment.rating} size={12} />}
                    <span className="text-xs text-muted-foreground">{timeAgo(comment.created_at)}</span>
                  </div>
                </div>

                {/* Text */}
                <p className="text-xs text-foreground/80 leading-relaxed">{comment.content}</p>

                {/* Like */}
                <div className="flex items-center gap-2 pt-1 border-t border-border">
                  <LikeButton
                    targetType="comment"
                    targetId={comment.id}
                    initialCount={comment.likes_count}
                    size={11}
                    showCount={true}
                  />
                  <span className="text-xs text-muted-foreground">helpful</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}